// Head-office (multi-branch) reads from the shared Supabase project.
//
// Each shop's LAN server pushes its data up to the cloud (see server/cloud.js):
// a heartbeat row per branch, one row per sale, and a periodic products/stock
// snapshot. This module reads those back so the Head Office page can show
// consolidated sales and cross-branch stock. Reads only — branches are the sole
// writers (via their LAN server). We use PostgREST over plain `fetch`, sharing
// the connection in services/supabase.ts (no SDK dependency).
//
// One-time Supabase setup (run in the dashboard → SQL editor; the anon key can't
// create tables):
//
//   create table if not exists public.pos_branches (
//     branch_id   text primary key,
//     name        text,
//     last_seen   timestamptz not null default now(),
//     seq         bigint default 0,
//     today_total numeric default 0
//   );
//   create table if not exists public.pos_sales (
//     branch_id  text not null,
//     sale_id    text not null,
//     bill_no    text,
//     ts         timestamptz,
//     total      numeric,
//     status     text,
//     data       jsonb,
//     updated_at timestamptz not null default now(),
//     primary key (branch_id, sale_id)
//   );
//   create index if not exists pos_sales_branch_ts on public.pos_sales (branch_id, ts desc);
//   create index if not exists pos_sales_ts on public.pos_sales (ts desc);
//   create table if not exists public.pos_branch_products (
//     branch_id  text primary key,
//     name       text,
//     data       jsonb,
//     updated_at timestamptz not null default now()
//   );
//   alter table public.pos_branches        enable row level security;
//   alter table public.pos_sales           enable row level security;
//   alter table public.pos_branch_products enable row level security;
//   create policy "pos_branches anon"        on public.pos_branches        for all to anon using (true) with check (true);
//   create policy "pos_sales anon"           on public.pos_sales           for all to anon using (true) with check (true);
//   create policy "pos_branch_products anon" on public.pos_branch_products for all to anon using (true) with check (true);
//
// Security note (same tier as cloud backup): with the publishable anon key +
// permissive policies, anyone with the project URL/key can read/write these
// tables. Acceptable for a private multi-branch deployment; the hardening phase
// would move writes behind a signed token.

import { Product } from '../types';
import { REST, sbHeaders, sbError } from './supabase';

export interface BranchRow {
  branch_id: string;
  name: string;
  last_seen: string;
  seq: number;
  today_total: number;
}

export interface CloudSaleRow {
  branch_id: string;
  bill_no: string;
  ts: string;
  total: number;
  status: string;
}

export interface BranchProductsRow {
  branch_id: string;
  name?: string;
  data: Product[];
  updated_at: string;
}

async function fail(res: Response): Promise<never> {
  const err = await sbError(res);
  if (res.status === 404 || /does not exist|pos_branches|pos_sales/.test(err.message)) {
    throw new Error(
      'Head-office tables not found. Create pos_branches / pos_sales / pos_branch_products in ' +
        'Supabase first (see setup SQL in headOffice.ts).'
    );
  }
  throw err;
}

// Every branch that has ever checked in, most-recently-seen first.
export async function listBranches(): Promise<BranchRow[]> {
  const res = await fetch(`${REST}/pos_branches?select=*&order=last_seen.desc`, {
    headers: sbHeaders(),
  });
  if (!res.ok) await fail(res);
  return (await res.json()) as BranchRow[];
}

// All sales across every branch in the [fromISO, toISO] window.
export async function getSalesRange(fromISO: string, toISO: string): Promise<CloudSaleRow[]> {
  const url =
    `${REST}/pos_sales?select=branch_id,bill_no,ts,total,status` +
    `&ts=gte.${encodeURIComponent(fromISO)}&ts=lte.${encodeURIComponent(toISO)}` +
    `&order=ts.desc&limit=10000`;
  const res = await fetch(url, { headers: sbHeaders() });
  if (!res.ok) await fail(res);
  return (await res.json()) as CloudSaleRow[];
}

// Latest products/stock snapshot for each branch (for cross-branch stock views).
export async function getBranchProducts(): Promise<BranchProductsRow[]> {
  const res = await fetch(`${REST}/pos_branch_products?select=branch_id,name,data,updated_at`, {
    headers: sbHeaders(),
  });
  if (!res.ok) await fail(res);
  return (await res.json()) as BranchProductsRow[];
}

// ---- Consolidation helpers ------------------------------------------------

export interface BranchSalesSummary {
  branchId: string;
  bills: number;
  total: number;
}

// Group a flat sales list into per-branch totals (cancelled bills excluded),
// plus the combined figure.
export function summariseByBranch(rows: CloudSaleRow[]): {
  perBranch: BranchSalesSummary[];
  totalBills: number;
  totalAmount: number;
} {
  const map = new Map<string, BranchSalesSummary>();
  let totalBills = 0;
  let totalAmount = 0;
  for (const r of rows) {
    if (r.status === 'cancelled') continue;
    const s = map.get(r.branch_id) ?? { branchId: r.branch_id, bills: 0, total: 0 };
    s.bills += 1;
    s.total += Number(r.total) || 0;
    map.set(r.branch_id, s);
    totalBills += 1;
    totalAmount += Number(r.total) || 0;
  }
  return {
    perBranch: Array.from(map.values()).sort((a, b) => b.total - a.total),
    totalBills,
    totalAmount,
  };
}
